import { pushDataEvent } from 'dotcom-core/src/helpers/tracking';
import {
  NIButtonAudioIcon,
  NIButtonPauseIcon,
} from '@native-instruments/webex-ni';
import clsx from 'clsx';
import type {
  HTMLProps,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
} from 'react';
import { useCallback, useMemo } from 'react';
import { Link, useLoaderData } from 'react-router';
import { Img } from '~/components/Img';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';
import type { AlgoliaProductHit } from '~/helpers/product';
import { useAudioPlayer } from './AudioPlayer';

type ProductCardProps = {
  hit: AlgoliaProductHit;
  index: number;
  queryId?: string;
  nativeId?: string;
  storeCode: string;
} & HTMLProps<HTMLDivElement>;

const NATIVE_DOMAIN = 'https://native-instruments.com';
const IZOTOPE_DOMAIN = 'https://izotope.com'; // For audio songs, not yet available to native-instruments domain.

export function ProductCard({
  hit: {
    objectID,
    is_free,
    product_name,
    description,
    featured_image_variants,
    prices,
    web_tour_page,
    pdp_url,
    audios,
    item_type,
    master_sku,
  },
  index,
  storeCode,
}: ProductCardProps) {
  const { tPrice, t } = useIntl();
  const { startPlaylist, currentPlaylist, isPlaying } = useAudioPlayer();
  const { queryId, nativeId } = useLoaderData();

  const isHardware = item_type === 'Hardware' || item_type === 'Accessories';
  const price = prices?.[storeCode];

  const [_, currency] = storeCode.split('_');

  const showSavings =
    price?.full_version_final_price !== price?.full_version_price &&
    isTruthy(price?.full_version_price);

  const FallbackImg = (
    <div className="bg-grey-20 w-full h-full flex justify-center items-center" />
  );

  const playlist = useMemo(() => {
    return audios
      .filter(({ audio_id }, index, array) => {
        return array.findIndex((val) => val.audio_id === audio_id) === index;
      })
      .map(({ uri, title, description }) => ({
        title,
        description,
        uri: `${IZOTOPE_DOMAIN}/${uri}`,
      }));
  }, [audios]);

  const handleAudioPlayer = (
    event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    startPlaylist(objectID, playlist);
  };

  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    pushDataEvent({
      event: 'catalog_item_click',
      queryId,
      position: index,
      objectID,
      nativeId,
    });
  }, [objectID, nativeId, index, queryId]);

  const handleKeyUp = useCallback<KeyboardEventHandler<HTMLAnchorElement>>(
    (event) => {
      if (event.key !== 'Enter') return;

      pushDataEvent({
        event: 'catalog_item_click',
        queryId,
        position: index,
        objectID,
        nativeId,
      });
    },
    [objectID, nativeId, index, queryId],
  );

  const isCurrentPlayer = currentPlaylist === objectID;

  return (
    <div
      data-testid={`product-card-${master_sku}`}
      className={clsx(
        'group flex flex-col bg-white',
        'w-full min-w-[288px]',
        'relative cursor-pointer',
        'transition-shadow duration-500 hover:shadow-card',
      )}
    >
      <Link
        data-testid="card-link"
        to={web_tour_page}
        className="md:aspect-[380/248] bg-grey-20 rounded-t overflow-hidden relative"
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        discover="none"
        reloadDocument={true}
      >
        {isTruthy(featured_image_variants) ? (
          <picture>
            {featured_image_variants.src_set
              .map((image) => (
                <source
                  key={image?.width}
                  media={`(min-width: ${image?.width}px), ${image?.pixel_ratio}`}
                  srcSet={`${NATIVE_DOMAIN}${image.url}`}
                  type="image/webp"
                />
              ))
              .reverse()}

            <Img
              fallback={FallbackImg}
              width="100%"
              className="transition-transform duration-[0.5s] ease group-hover:scale-[1.05]"
              src={`${NATIVE_DOMAIN}${featured_image_variants.url}`}
              alt={featured_image_variants.alt_text}
              data-testid="card-image"
            />
          </picture>
        ) : (
          FallbackImg
        )}

        {audios.length > 0 && (
          <>
            <div className="absolute top-0 bottom-0 left-0 right-0" />
            <div className="absolute bottom-0 flex justify-center w-full p-2">
              <button
                data-testid="play-audio-button"
                onClick={handleAudioPlayer}
                className="cursor-pointer"
              >
                {isPlaying && isCurrentPlayer ? (
                  <NIButtonPauseIcon
                    data-testid="audio-playing"
                    className="w-4 h-4 transition-colors ease-out duration-0.15 text-white hover:text-grey-20"
                  />
                ) : (
                  <NIButtonAudioIcon
                    data-testid="audio-stopped"
                    className="w-4 h-4 transition-colors ease-out duration-0.15 text-white hover:text-grey-20"
                  />
                )}
              </button>
            </div>
          </>
        )}
      </Link>

      <div className="flex flex-col justify-between flex-1 border-b border-x border-grey-40 rounded-b px-2 py-2.5">
        <Link
          to={web_tour_page}
          className="flex flex-col items-center"
          onClick={handleClick}
          onKeyUp={handleKeyUp}
          discover="none"
          reloadDocument={true}
        >
          {/*
                See issue: https://soundwide.atlassian.net/browse/CXPD-151.
                <NILogoSimpleIcon className="w-[14px]" />
            */}

          <div
            data-testid="card-name"
            className="my-1 text-lg w-[288px] font-medium text-center truncate"
          >
            {product_name}
          </div>
          <div
            data-testid="card-desc"
            className="text-center text-xs text-ellipsis"
          >
            {description}
          </div>
        </Link>

        {is_free ? (
          <div className="mt-2 flex h-full flex-col items-center justify-end ">
            <span
              data-testid="card-free-details"
              className="font-medium text-sm text-savings"
            >
              {t('productCard.fullVersion', {
                fallback: 'Free trial / Free download',
              })}
            </span>
          </div>
        ) : (
          <div
            data-testid="card-price-listing"
            className="w-full flex items-end h-full justify-around mt-2"
          >
            {isTruthy(price?.smallest_variant_final_price) && (
              <div className="flex flex-col items-center">
                <div className="text-2xs">
                  {t('components.ProductPrice.license.update', {
                    fallback: 'Update',
                  })}
                </div>

                <Link
                  to={pdp_url}
                  data-testid="update-price"
                  className="text-sm font-medium hover:underline"
                  onClick={handleClick}
                  onKeyUp={handleKeyUp}
                >
                  {tPrice({
                    price: {
                      value: price.smallest_variant_final_price,
                      currency,
                    },
                  })}
                </Link>

                {price.smallest_variant_price !==
                  price.smallest_variant_final_price &&
                  isTruthy(price.smallest_variant_price) && (
                    <span data-testid="previous-price">
                      <div className="text-2xs text-grey-60 h-2">
                        <span className="line-through">
                          {tPrice({
                            price: {
                              value: price.smallest_variant_price,
                              currency,
                            },
                          })}
                        </span>
                      </div>
                    </span>
                  )}
              </div>
            )}

            <div className="flex flex-col items-center">
              {!isHardware && (
                <div className="text-2xs">
                  {t('components.ProductPrice.license.full-version', {
                    fallback: 'Full version',
                  })}
                </div>
              )}
              <Link
                to={pdp_url}
                data-testid="final-price"
                className="text-sm font-medium hover:underline"
                onClick={handleClick}
                onKeyUp={handleKeyUp}
              >
                {isTruthy(price?.full_version_final_price) &&
                  tPrice({
                    price: { value: price.full_version_final_price, currency },
                  })}
              </Link>

              {showSavings && (
                <span data-testid="previous-price">
                  <div className="text-2xs text-grey-60 h-2">
                    <span className="line-through">
                      {tPrice({
                        price: { value: price.full_version_price, currency },
                      })}
                    </span>
                  </div>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
